import { GetLeagueStandingsAsync } from '@maxpreps/apiserviceclients/clients/LeaguesServiceClient';
import { GetTeamStandingsAsync } from '@maxpreps/apiserviceclients/clients/StandingsServiceClient';
import { getTeamSchedule } from '@maxpreps/shared/data/ghost-apis/gateways-api';
import {
  tryCreateGSSPResponseObjectFromAPIResult,
  tryCreateGSSPResponseObjectFromObject
} from '@maxpreps/shared/functions/server/getServerSidePropsHelpers';
import handleGSSP from '@maxpreps/shared/functions/server/handleGSSP';
import executeInParallel from '@maxpreps/shared/functions/utils/executeInParallel';
import getSportDefinition from '@maxpreps/shared/functions/utils/getSportDefinition';
import { GetServerSidePropsContext } from 'next';
import { NIL as EMPTY_GUID } from 'uuid';
import { IScheduleIndexProps } from '../../pages/schedule';
import pageConfig from '../../pages_meta/schedule-index.meta';
import projectConfig from '../../project.meta';
import getPageTitle from '../../utils/getPageTitle';
import serializeSchedule from '../../utils/schedule/serializeSchedule';
import appGetServerSideProps, {
  ITeamAppServerSideProps
} from '../appGetServerSideProps';

export { pageConfig };

const getPageDescription = team => {
  const { defaultGender } = getSportDefinition(team.sport.toLowerCase());

  return `View the ${team.year} ${team.schoolName} ${
    team.gender.toLowerCase() === defaultGender
      ? ''
      : `${team.gender.toLowerCase()} `
  }${team.level
    .toLowerCase()
    .replace('jv', 'JV')} ${team.sport.toLowerCase()} team schedule.`;
};

const gssp = async (
  nextContext: GetServerSidePropsContext
): Promise<Error | IScheduleIndexProps> => {
  const { schoolid, ssid, admin } = nextContext.query;

  const isAdmin = admin === '1';

  let results: any[] = await executeInParallel(
    [
      {
        method: appGetServerSideProps,
        params: [nextContext, pageConfig],
        onResult: tryCreateGSSPResponseObjectFromObject
      },
      {
        method: getTeamSchedule,
        params: [schoolid, ssid]
      },
      {
        method: GetTeamStandingsAsync,
        params: [schoolid, ssid]
      }
    ],
    {
      defaultOnResultCallback: tryCreateGSSPResponseObjectFromAPIResult
    }
  );

  if (results[0]) {
    return results[0];
  }

  const [, appGetServerSidePropsData, [teamScheduleData], [teamStandingsData]] =
    results;

  const { teamContext } =
    appGetServerSidePropsData as unknown as ITeamAppServerSideProps;

  let leagueStandingsData;
  if (teamContext?.data?.leagueId && EMPTY_GUID !== teamContext.data.leagueId) {
    results = await executeInParallel(
      [
        {
          method: GetLeagueStandingsAsync,
          params: [
            teamContext?.data?.leagueId,
            EMPTY_GUID // Will try to use "null" if not provided
          ]
        }
      ],
      {
        ensureResultsAreArrays: true,
        defaultOnResultCallback: tryCreateGSSPResponseObjectFromAPIResult
      }
    );

    if (results[0]) {
      return results[0];
    }

    [, [leagueStandingsData]] = results;
  }

  const feedbackLink = isAdmin
    ? 'https://forms.gle/B9AXrhoJQF9vQFeh9'
    : 'https://forms.gle/bvNCe3M4LMkhkcM38';

  return {
    ...appGetServerSidePropsData,
    contests: serializeSchedule(teamScheduleData, schoolid as string),
    standings: teamStandingsData,
    leagueStandings:
      leagueStandingsData?.find(s => s.schoolId === schoolid) ?? null,
    feedbackLink,
    pageConfig,
    pageTitle: getPageTitle(pageConfig, teamContext.data),
    pageDescription: getPageDescription(teamContext.data)
  };
};

const pageGSSPWrapper = async nextContext =>
  handleGSSP(nextContext, projectConfig, pageConfig, [[gssp]]);

export default pageGSSPWrapper;
